import { useMemo, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { DropdownButton, Form, NavDropdown } from "react-bootstrap";
import ReactDatePicker from "react-datepicker";
import { Controller, get, useFormContext, useWatch } from "react-hook-form";
import { listFormatter } from "../../../utils/listFormatter";
import { NON_METRIC_FIELD_ACCESSORS, RELATIVE_METRIC_FIELD_ACCESSORS } from "./constants";
import { getMetricNumber } from "./labels";
import { useProgramContext } from "./ProgramContext";
import { CustomTriggerTooltip } from "../../core/basic/Tooltip";
import { cx } from "../../../../styled-system/css";

const Label = ({ toolTip = "", children, className = "" }) => {
  return (
    <Form.Label className={cx("mb-3", className)}>
      <h6
        className={`title fw-bold fs-4`}
        style={{
          textTransform: "none",
        }}
      >
        {toolTip ?
          <CustomTriggerTooltip toolTip={toolTip}>{children}</CustomTriggerTooltip>
        : children}
      </h6>
    </Form.Label>
  );
};

// Binds rhf and internal state
const MetricCompareSelectField = ({ fieldName, control, rules, metricOptions, isDisabled, onOptionChange }) => {
  return (
    <Controller
      name={fieldName}
      control={control}
      rules={rules}
      render={({ field }) => {
        return (
          <MetricCompareSelect
            {...field}
            metricOptions={metricOptions}
            isDisabled={isDisabled}
            onOptionChanged={onOptionChange}
          />
        );
      }}
    />
  );
};

const MetricCompareSelect = ({ value, onChange, metricOptions = [], isDisabled = false, onOptionChanged }) => {
  const { t } = useTranslation(["programs"]);
  const [operator, metric] = useMemo(() => {
    return value ? JSON.parse(value) : [null, null];
  }, [value]);

  const metricName = metric.replaceAll(/(^[^a-zA-Z0-9]+)|([^a-zA-Z0-9]+$)/g, "");

  const supportedOperators = metricOptions?.find((o) => o.value === metric)?.supportedOperators;

  const onMetricChange = (newMetric) => {
    // Below makes sure operator applies to newMetric, otherwise choose first applicable operator
    const supportedOperators = metricOptions?.find((o) => o.value === newMetric)?.supportedOperators;

    const newOperator =
      supportedOperators?.map((o) => o.value)?.includes(operator) ? operator : supportedOperators[0]?.value;
    const newOption = JSON.stringify([newOperator, newMetric]);
    onChange(newOption);
    onOptionChanged?.(newOption);
  };

  const onOperatorChange = (newOperator) => {
    const newOption = JSON.stringify([newOperator, metric]);
    onChange(newOption);
    onOptionChanged?.(newOption);
  };

  const linkingVerb = useMemo(() => {
    return getMetricNumber(metricName) === "plural" ? t(($) => $.programs.fields.are) : t(($) => $.programs.fields.is);
  }, [metricName, t]);

  const resolveLabel = (label) => (typeof label === "function" ? t(label) : label);

  const selectedOption = metricOptions.find((o) => o.value === metric);

  return (
    <div className="d-flex">
      {/*  Metric */}
      <NavDropdown
        bsPrefix="inline-dropdown"
        title={
          resolveLabel(selectedOption?.activeLabel ?? selectedOption?.label) ??
          t(($) => $.programs.fields["Select Metric"])
        }
        disabled={isDisabled}
      >
        <div style={{ maxHeight: "250px" }}>
          {metricOptions?.map((option) => (
            <NavDropdown.Item
              key={option.value}
              onClick={() => {
                const { value } = option;
                onMetricChange(value);
              }}
            >
              {resolveLabel(option.label)}
            </NavDropdown.Item>
          ))}
        </div>
      </NavDropdown>

      {!NON_METRIC_FIELD_ACCESSORS.includes(metricName) && (
        <>
          <span className={"px-3"}>{linkingVerb}</span>
          <NavDropdown
            bsPrefix="inline-dropdown"
            title={(() => {
              const operatorLabel = supportedOperators?.find((o) => o.value === operator)?.label;
              return typeof operatorLabel === "function" ? t(operatorLabel) : operatorLabel;
            })()}
            disabled={isDisabled}
          >
            <div style={{ maxHeight: "250px" }}>
              {supportedOperators?.map((option) => (
                <NavDropdown.Item
                  key={option.value}
                  onClick={() => {
                    const { value } = option;
                    onOperatorChange(value);
                  }}
                >
                  {typeof option.label === "function" ? t(option.label) : option.label}
                </NavDropdown.Item>
              ))}
            </div>
          </NavDropdown>
        </>
      )}
    </div>
  );
};

const InputField = ({
  label,
  toolTip,
  placeholder,
  fieldName,
  control,
  rules,
  disabled,
  align = "left",
  shouldUnregister = false,
  format,
  type = "text",
  step = null,
  errors,
  errorClassNames = "",
  symbol = null,
}) => {
  const { t } = useTranslation(["programs"]);
  const error = get(errors, fieldName);

  const rulesWithFormat = useMemo(() => {
    if (format === "number") {
      return {
        ...rules,
        validate: (val) => {
          // allow positive and negative numbers
          if (format === "number" && typeof val === "string") {
            const isValid = /^-?\d*\.?\d*$/.test(val);
            if (!isValid) {
              return t(($) => $.programs.fields["Please enter a valid number"]);
            }
          }
          return true;
        },
      };
    }

    return rules;
  }, [format, rules, t]);

  return (
    <Form.Group>
      {label && <Label toolTip={toolTip}>{label}</Label>}
      <Controller
        name={fieldName}
        control={control}
        rules={rulesWithFormat}
        shouldUnregister={shouldUnregister}
        render={({ field, fieldState: { error } }) => {
          return (
            <span
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "0.5rem",
              }}
            >
              {/* Guard on truthiness: `symbol` defaults to null, and an empty <span> would be a
                  flex item, costing a 0.5rem gap the bare text never paid. */}
              {symbol && symbol !== "%" ?
                <span>{symbol}</span>
              : null}
              <Form.Control
                isInvalid={!!error}
                placeholder={placeholder}
                disabled={disabled}
                style={{ textAlign: align, minWidth: "50px" }}
                type={type}
                step={step}
                {...field}
              />
              {symbol === "%" ?
                <span>{symbol}</span>
              : null}
            </span>
          );
        }}
      />
      {error && (
        <span className={`text-danger ${errorClassNames}`} style={{ marginLeft: "2rem" }}>
          <br />
          {error?.message}
        </span>
      )}
    </Form.Group>
  );
};

const HiddenField = ({ fieldName, control, rules, defaultValue }) => {
  return (
    <Controller
      name={fieldName}
      control={control}
      rules={rules}
      shouldUnregister={true}
      defaultValue={defaultValue}
      render={({ field }) => {
        return <input type="hidden" {...field} />;
      }}
    />
  );
};

const InlineInputField = ({ width = "8rem", format, type, rules, isDisabled = false, ...props }) => {
  return (
    <div style={{ maxWidth: width, position: "relative" }}>
      <InputField {...props} align="center" format={format} type={type} rules={rules} disabled={isDisabled} />
    </div>
  );
};

const InlineInputSelectComboField = ({
  metricCompareValueOptions,
  control,
  rules,
  fieldName,
  symbol,
  isDisabled = false,
}) => {
  return (
    <Controller
      name={fieldName}
      control={control}
      rules={rules}
      render={({ field }) => {
        return (
          <InlineInputComboSelect
            value={field.value}
            onChange={field.onChange}
            metricCompareValueOptions={metricCompareValueOptions}
            symbol={symbol}
            isDisabled={isDisabled}
          />
        );
      }}
    />
  );
};

const InlineInputComboSelect = ({ value, onChange, metricCompareValueOptions, symbol, isDisabled = false }) => {
  const { t } = useTranslation(["programs"]);
  const isRelative = (value) => metricCompareValueOptions?.some((o) => o.value !== "value" && o.value === value);
  // Derived from the value rather than kept in state, so it can never disagree with what the form will save.
  const compareOption = isRelative(value) ? value : "value";

  const resolveLabel = (label) => (typeof label === "function" ? t(label) : label);

  return (
    <div className={`d-flex align-items-center rules-engine-combo-select`}>
      {compareOption === "value" ?
        <div style={{ maxWidth: "8rem", position: "relative" }}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "0.5rem",
            }}
          >
            {symbol && symbol !== "%" ?
              <span>{symbol}</span>
            : null}
            <Form.Control
              style={{ textAlign: "center" }}
              value={value}
              onChange={(e) => {
                onChange(e.target.value);
              }}
              disabled={isDisabled}
            />
            {symbol === "%" ?
              <span>{symbol}</span>
            : null}
          </div>
        </div>
      : <Form.Control
          value={resolveLabel(metricCompareValueOptions?.find((o) => o.value === compareOption)?.label)}
          style={{
            width: "250px",
            textAlign: "center",
            position: "relative",
          }}
          disabled={true}
        />
      }

      <NavDropdown
        style={symbol === "%" ? { marginRight: "-2px", marginLeft: "2px" } : { marginRight: "-10px" }}
        disabled={isDisabled}
      >
        {metricCompareValueOptions?.map((option) => {
          const { label, value: compareValue } = option;
          return (
            <div key={compareValue} className="text-dark-purple">
              <NavDropdown.Item
                id={compareValue}
                onClick={() => {
                  if (compareValue !== "value") {
                    onChange(compareValue);
                  } else if (isRelative(value)) {
                    onChange(10);
                  }
                }}
              >
                {resolveLabel(label)}
              </NavDropdown.Item>
            </div>
          );
        })}
      </NavDropdown>
    </div>
  );
};

const InlineSelect = ({
  options,
  fieldName,
  control,
  rules,
  disabled,
  title = null,
  defaultValue = null,
  onChange: onChangeProp,
}) => {
  const { t } = useTranslation(["programs"]);

  return (
    <Form.Group>
      <Controller
        defaultValue={defaultValue}
        name={fieldName}
        control={control}
        rules={rules}
        render={({ field }) => {
          const selectedOption = options.find((option) => option.value === field.value);
          return (
            <NavDropdown
              bsPrefix="inline-dropdown"
              title={selectedOption?.label ?? title ?? t(($) => $.programs.fields.Select)}
              disabled={disabled}
            >
              <div style={{ maxHeight: "250px" }}>
                {options?.map((option) => (
                  <NavDropdown.Item
                    key={option.value}
                    onClick={() => {
                      field.onChange(option.value);
                      onChangeProp?.(option.value);
                    }}
                  >
                    {option.label}
                  </NavDropdown.Item>
                ))}
              </div>
            </NavDropdown>
          );
        }}
      />
    </Form.Group>
  );
};

// Multi-select

const disjunctionFormatter = new Intl.ListFormat("en", {
  style: "long",
  type: "disjunction",
});

const InlineMultiSelect = ({ options = [], fieldName, control, rules, disabled, description, useOr = false }) => {
  const { t } = useTranslation(["programs"]);
  return (
    <Form.Group className="ms-0">
      <Controller
        name={fieldName}
        control={control}
        rules={rules}
        render={({ field }) => {
          const selectedOptions = options.filter((option) => {
            if (Array.isArray(field.value)) {
              return field.value?.includes(option.value);
            }
            return false;
          });

          const onToggleMultiSelectValue = ({ id, checked }) => {
            // Below type check is hacky, but handles case where condition changed from numeric value to array of strings
            // In this case, if current value isn't compatible, it will start from an empty array
            const currentSelections =
              typeof field.value === "number" ? []
              : typeof field.value === "string" ? [field.value]
              : field.value;
            let updatedSelections = currentSelections ? [...currentSelections] : [];

            if (currentSelections?.includes(id) && checked === false) {
              updatedSelections = updatedSelections.filter((o) => o !== id);
            }

            if (!currentSelections?.includes(id) && checked) {
              updatedSelections.push(id);
            }

            field.onChange(updatedSelections);
          };

          const resolveLabel = (label) => (typeof label === "function" ? t(label) : label);

          return (
            <DropdownButton
              renderMenuOnMount={true}
              flip={false}
              bsPrefix="col-selector inline-dropdown"
              title={
                <span className="text-dark-purple text-decoration-underline">
                  {selectedOptions.length ?
                    (useOr ? disjunctionFormatter : listFormatter).format(
                      selectedOptions?.map((o) => resolveLabel(o?.label)),
                    )
                  : t(($) => $.programs.fields["Select {description}"], { description })}
                </span>
              }
              disabled={disabled}
              className="ms-0"
            >
              {options.map(({ id, label, value }) => {
                const checked = Array.isArray(field.value) ? field.value?.includes(value) : false;
                return (
                  <div key={id} role="menuitem" className="py-1">
                    <label
                      style={{
                        marginLeft: "1rem",
                        marginTop: "0.5rem",
                        fontWeight: "normal",
                        display: "flex",
                        whiteSpace: "nowrap",
                      }}
                    >
                      <input
                        type="checkbox"
                        style={{ height: "unset", marginRight: "1rem" }}
                        onChange={(e) => {
                          e.stopPropagation();

                          onToggleMultiSelectValue({
                            id: id,
                            checked: e.target.checked,
                          });
                        }}
                        checked={checked}
                      />
                      {resolveLabel(label)}
                    </label>
                  </div>
                );
              })}
            </DropdownButton>
          );
        }}
      />
    </Form.Group>
  );
};

const DateField = ({
  label,
  toolTip,
  placeholder,
  fieldName,
  errors,
  control,
  rules,
  disabled,
  errorClassNames = "",
  disableKeyboardInput = false,
  minDate,
  maxDate,
  showTimeInput = false,
}) => {
  const { t } = useTranslation(["programs"]);
  const error = get(errors, fieldName);

  return (
    <Form.Group>
      {label && <Label toolTip={toolTip}>{label}</Label>}
      <div>
        <Controller
          name={fieldName}
          control={control}
          rules={rules}
          render={({ field }) => {
            const date = field.value && new Date(Date.parse(field.value));

            return (
              <ReactDatePicker
                className={`form-control w-100 ${error && "is-invalid"}`}
                placeholderText={placeholder}
                disabled={disabled}
                selected={date}
                onKeyDown={(e) => {
                  if (disableKeyboardInput) {
                    e.preventDefault();
                  }
                }}
                minDate={minDate}
                timeFormat="HH:mm:ss"
                dateFormat={showTimeInput ? "MMM d, yyyy h:mm aa" : "MM/dd/yyyy"}
                maxDate={maxDate}
                onChange={(date) => (date ? field.onChange(date.toISOString()) : field.onChange(null))}
                showTimeInput={showTimeInput}
                timeInputLabel={showTimeInput ? t(($) => $.programs.fields["Time:"]) : null}
              />
            );
          }}
        />
        {error && (
          <span className={`text-danger ${errorClassNames}`} style={{ marginLeft: "2rem" }}>
            <br />
            {error?.message}
          </span>
        )}
      </div>
    </Form.Group>
  );
};

// return lists of components to render out for condition based on selectedOption
// These inputs are keyed by the option, so changing the metric or operator remounts them. They must not
// unregister: the new input reads the value before the old one's unmount deletes it, leaving the form holding
// a value other than the one shown. ConditionForm trims the values to fit the new option instead.
const ConditionValues = ({
  fieldName,
  control,
  conditionOptions,
  selectedOption,
  accessor,
  symbol,
  isDisabled = false,
}) => {
  const { t } = useTranslation(["programs"]);
  const [operator, metric] = selectedOption ? JSON.parse(selectedOption) : [null, null];
  const metricName = accessor?.replaceAll(/(^[^a-zA-Z0-9]+)|([^a-zA-Z0-9]+$)/g, "") ?? "";
  const { engine } = useProgramContext();
  const { clearErrors, resetField } = useFormContext();

  useEffect(() => {
    clearErrors(`${fieldName}`);
  }, [metric, clearErrors, fieldName, resetField]);

  if (!operator) {
    return null;
  }

  if (metricName === "query") {
    return (
      <InlineInputField
        key={`${selectedOption}-${operator}`}
        fieldName={`${fieldName}.values.0`}
        defaultValue={""}
        control={control}
        rules={{ required: true }}
        placeholder={null}
        format={"text"}
        symbol={symbol}
        width="250px"
        isDisabled={isDisabled}
      />
    );
  }

  if (
    accessor.includes("target_match_types") ||
    accessor.includes("keyword_match_types") ||
    accessor.includes("tags_enhanced")
  ) {
    const isTagsEnhanced = accessor.includes("tags_enhanced");
    return (
      <>
        <span className="px-3">
          {isTagsEnhanced ? t(($) => $.programs.fields.include) : t(($) => $.programs.fields.with)}
        </span>
        <span className="px-3">
          <InlineMultiSelect
            key={selectedOption}
            description={isTagsEnhanced ? t(($) => $.programs.fields.Tags) : t(($) => $.programs.fields["Match Types"])}
            fieldName={`${fieldName}.values`}
            control={control}
            rules={{ required: true }}
            options={conditionOptions?.find((o) => o.value === accessor)?.selectOptions}
            disabled={isDisabled}
            useOr={isTagsEnhanced}
          />
        </span>
        {!isTagsEnhanced && <span className="px-3">{t(($) => $.programs.fields["match types"])}</span>}
      </>
    );
  }

  const relativeFields = RELATIVE_METRIC_FIELD_ACCESSORS[engine] ?? [];

  if (relativeFields.map((relativeField) => relativeField?.accessor)?.some((metric) => accessor.includes(metric))) {
    const compareValueOptions = relativeFields?.find((metric) => metric.accessor === metricName)?.options ?? [];

    if (["between"].includes(operator)) {
      return (
        <>
          <InlineInputSelectComboField
            key={`${selectedOption}-start-combo`}
            fieldName={`${fieldName}.values.0`}
            control={control}
            rules={{ required: true }}
            metricCompareValueOptions={[
              { value: "value", label: t(($) => $.programs.fields.Value) },
              ...compareValueOptions,
            ]}
            symbol={symbol}
            isDisabled={isDisabled}
          />
          <span className="px-3">{t(($) => $.programs.fields.and)}</span>
          <InlineInputSelectComboField
            key={`${selectedOption}-end-combo`}
            fieldName={`${fieldName}.values.1`}
            control={control}
            rules={{ required: true }}
            metricCompareValueOptions={[
              { value: "value", label: t(($) => $.programs.fields.Value) },
              ...compareValueOptions,
            ]}
            symbol={symbol}
            isDisabled={isDisabled}
          />
        </>
      );
    }

    return (
      <InlineInputSelectComboField
        key={`${selectedOption}-combo`}
        fieldName={`${fieldName}.values.0`}
        control={control}
        rules={{ required: true }}
        metricCompareValueOptions={[
          { value: "value", label: t(($) => $.programs.fields.Value) },
          ...compareValueOptions,
        ]}
        symbol={symbol}
        isDisabled={isDisabled}
      />
    );
  }

  if ([">", "<", ">=", "<=", "="].includes(operator)) {
    return (
      <InlineInputField
        key={`${selectedOption}-${operator}`}
        fieldName={`${fieldName}.values.0`}
        control={control}
        rules={{ required: true }}
        placeholder={null}
        format={"number"}
        symbol={symbol}
        width={symbol && symbol !== "%" ? "120px" : undefined}
        isDisabled={isDisabled}
      />
    );
  }

  if (["between"].includes(operator)) {
    return (
      <>
        <InlineInputField
          key={`${selectedOption}-start`}
          fieldName={`${fieldName}.values.0`}
          control={control}
          rules={{ required: true }}
          placeholder={null}
          format={"number"}
          symbol={symbol}
        />
        <span className="px-3">{t(($) => $.programs.fields.and)}</span>
        <InlineInputField
          key={`${selectedOption}-end`}
          fieldName={`${fieldName}.values.1`}
          control={control}
          rules={{ required: true }}
          placeholder={null}
          format={"number"}
          symbol={symbol}
        />
      </>
    );
  }

  return null;
};

// Not unregistered on unmount either: remounted, it would show nothing while the form restores the saved days.
// ConditionForm drops the scope when the metric has none.
const ConditionScope = ({ fieldName, control, isDisabled }) => {
  const { t } = useTranslation(["programs"]);
  const days = useWatch({
    name: `${fieldName}.scope.values.days`,
    control,
  });

  return (
    <>
      <span className={"px-3"}>{t(($) => $.programs.fields.over)}</span>
      <InlineInputField
        fieldName={`${fieldName}.scope.values.days`}
        control={control}
        rules={{ required: true }}
        placeholder={null}
        format={"number"}
        isDisabled={isDisabled}
      />
      <span className={"px-3"}>{t(($) => $.programs.fields["days"], { count: days ?? 0 })}</span>
    </>
  );
};

export {
  Label,
  InputField,
  DateField,
  InlineSelect,
  InlineInputField,
  MetricCompareSelectField,
  ConditionValues,
  ConditionScope,
  HiddenField,
};
